import React, { useEffect } from 'react';
import { FormFieldDependentSelect } from '@publishing-engine/domain/types/article';
import { Flex, Text } from '@chakra-ui/react';
import { RadioGroup } from '../RadioGroup.js';
import { useFormContext } from 'react-hook-form';
import { FormFeedback, Select } from '@morressier/alembic';
import { Field, FieldLabel } from 'src/components/Field';
import { IFetchOrganizationAndJournalsInfo } from 'src/api/queries';

interface FormFieldDependentSelectProps {
  config: FormFieldDependentSelect;
  organizationData: IFetchOrganizationAndJournalsInfo;
  currentSubmissionContextId: string;
}

export const SuitedForAnotherJournal: React.FC<
  FormFieldDependentSelectProps
> = ({ config, organizationData, currentSubmissionContextId }) => {
  const {
    formState: { errors },
    register,
    watch,
    control,
    setValue
  } = useFormContext();
  const {
    name,
    params: { labels, validations },
    caseDataFields: { main: field, dependsOn }
  } = config;
  const dependsOnField = `${field}.${dependsOn}` as const;
  const mainField = `${field}.value` as const;
  const mainLabel = labels.main.replace(
    '{publisherName}',
    organizationData.organization?.name
  );
  const sectionTitle = labels.section.title.replace(
    '{publisherName}',
    organizationData.organization?.name
  );

  const optionKeys = Object.keys(labels.options);

  const options = optionKeys.map(key => {
    return {
      value: key === 'true' ? true : false,
      children: (
        <Flex flexDirection="column">
          <Text textStyle="textBody">{labels.options[key]?.main}</Text>
          <Text textStyle="textBody">{labels.options[key]?.secondary}</Text>
        </Flex>
      )
    };
  });

  const otherJournals = organizationData.journals?.filter(
    journal => journal.id !== currentSubmissionContextId
  );

  const mainFieldOptions = otherJournals.map(journal => {
    return {
      main: journal.data.name,
      secondary: journal.data.name
    };
  });

  const error = errors[field];

  const exists = watch(dependsOnField);
  const fieldValue = watch(mainField);

  useEffect(() => {
    if (!exists) {
      setValue(mainField, '');
    }
  }, [exists]);

  return (
    <Flex flexDirection="column" gap={6}>
      <Field label={mainLabel} isRequired={validations.required}>
        <Text textStyle="textBody">{labels.subtitle}</Text>
        <Flex flexDirection="column">
          <RadioGroup
            name={dependsOnField}
            control={control}
            options={options}
            required={validations.required}
            gap={2}
          />
        </Flex>
        {exists ? (
          <Flex flexDirection="column" gap={2} width="100%">
            <FieldLabel
              label={sectionTitle}
              subText={<>{labels.section.explanation} </>}
              isRequired
              helpText={labels.helpText}
            />
            <Select
              id={name}
              {...register(mainField, { required: validations.required })}
              isInvalid={!!error}
              value={fieldValue || ''}
            >
              <option value="" disabled>
                Select an option
              </option>
              {Object.values(mainFieldOptions).map(option => (
                <option key={option.main} value={option.main}>
                  {option.main}
                </option>
              ))}
            </Select>
          </Flex>
        ) : null}
        {error && (
          <FormFeedback variant="error">
            {error.type === 'required'
              ? 'This field is required'
              : `Please select a valid ${sectionTitle} option `}
          </FormFeedback>
        )}
      </Field>
    </Flex>
  );
};
